import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { TFunction } from 'i18next';

import { Spinner } from '@/components/atoms/Spinner';
import PageLayout from '@/components/Layout/PageLayout';
import { ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { BioscopeMovingModalContainer } from '@/components/organisms/MoveBioscopeModal/MoveBioscopeModal';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { usePermissions } from '@/contexts/auth';
import { useDemoMode } from '@/contexts/demo/DemoContext';
import { useDeleteLastBioscopeMove, useGetClientBioscopes } from '@/domain/bioscopes';
import { getDemoDisplayName } from '@/utils/demoMasking';

import { MoveBioscope } from './MoveBioscope';

interface MoveBioscopeContainerProps {
    clientId: number;
    locationId?: number;
}
const showDeleteSuccessToast = (t: TFunction) => {
    ToastSuccess(t('moveBioscope.moveBioscope.deletionRegistered'), 'delete-last-move-success');
};

export function MoveBioscopeContainer({ clientId, locationId }: MoveBioscopeContainerProps) {
    const { t } = useTranslation();
    const routerLocation = useLocation();
    const { isDemoMode } = useDemoMode();
    const [moveBioscope, setMoveBioscope] = useState<number | null>(null);
    const [highlightedBioscopeId, setHighlightedBioscopeId] = useState<number | null>(null);
    const { canMoveBioscopes } = usePermissions();
    const internalPage = routerLocation.pathname.startsWith('/internal');

    const bioscopes = useGetClientBioscopes(clientId);
    useEffect(() => {
        if (highlightedBioscopeId) {
            const timer = setTimeout(() => {
                setHighlightedBioscopeId(null);
            }, 6000); // Highlight for 6 seconds
            return () => clearTimeout(timer);
        }
    }, [highlightedBioscopeId]);
    const handleDeleteSuccess = (bioscopeId: number) => {
        showDeleteSuccessToast(t);
        setHighlightedBioscopeId(bioscopeId);
    };
    const deleteLastBioscopeMove = useDeleteLastBioscopeMove({
        onSettled: () => bioscopes.refetch(),
    });

    const bioscopeToMove = bioscopes.data?.find((b) => b.id === moveBioscope);

    // Runs inside the table's confirmation, which shows a failure, so it rejects.
    const onDeleteBioscope = async (bioscopeId: number) => {
        await deleteLastBioscopeMove.mutateAsync(bioscopeId);
        handleDeleteSuccess(bioscopeId);
    };
    const data =
        bioscopes.data?.filter((bioscope) => {
            if (locationId == null) {
                return true;
            }

            return bioscope.lastLocation?.cage?.location?.id === locationId;
        }) ?? [];
    const canMoveBioscope = canMoveBioscopes(clientId, locationId);

    const mappedBioscopeData = data.map((bioscope) => {
        const cage = bioscope.lastLocation?.cage;
        const location = cage?.location;
        const shouldMaskNames = isDemoMode && !internalPage;

        return {
            id: bioscope.id,
            lastCage:
                shouldMaskNames && cage?.id != null
                    ? getDemoDisplayName(cage.id, 'cage', t)
                    : cage?.name,
            lastLocation:
                shouldMaskNames && location?.id != null
                    ? getDemoDisplayName(location.id, 'location', t)
                    : location?.name,
            lastMeasurement: bioscope.lastMeasurement?.timestamp,
            updatedAt: bioscope.lastLocation?.updatedAt,
        };
    });

    const handleMoveSuccess = () => {
        if (moveBioscope != null) {
            setHighlightedBioscopeId(moveBioscope);
        }
        setMoveBioscope(null);
        bioscopes.refetch();
    };

    return (
        <PageLayout
            title={
                canMoveBioscope
                    ? t('moveBioscope.moveBioscope.changePen')
                    : t('common.labels.bioscopes')
            }
            subtitle={t('moveBioscope.moveBioscope.oldTitleMoveBioscope')}>
            {bioscopes.error ? (
                <SomethingHappened {...retryFailedQueries(bioscopes)} />
            ) : bioscopes.isLoading ? (
                <Spinner size="lg" fullScreen />
            ) : (
                <MoveBioscope
                    canMoveBioscope={canMoveBioscope}
                    bioscopes={mappedBioscopeData}
                    onDeleteBioscope={onDeleteBioscope}
                    onMoveBioscope={(bioscopeId) =>
                        setMoveBioscope(Number.parseInt(bioscopeId.toString()))
                    }
                    highlightedBioscopeId={highlightedBioscopeId}
                />
            )}
            {moveBioscope && bioscopeToMove && (
                <BioscopeMovingModalContainer
                    isOpen={moveBioscope !== null}
                    onClose={() => setMoveBioscope(null)}
                    bioscopeId={bioscopeToMove.id}
                    onMoveSuccess={handleMoveSuccess}
                />
            )}
        </PageLayout>
    );
}
